import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Apple, Smartphone, BookOpen, LifeBuoy, Globe } from "lucide-react";
import { Button } from "../ui/button";
import { useI18n } from "../../i18n";

/**
 * RC-WEB-6 PHASE 14 — honest, dynamic help.
 * Store buttons exist only when the master admin configured a real URL
 * (`shared_content/app_links`). While nothing is published, the browser/PWA
 * experience is presented as what it is: the way AvisoCar works today.
 */

export const HelpSection = () => {
  const { t } = useI18n();
  const [links, setLinks] = useState(null);

  useEffect(() => {
    let alive = true;
    axios.get("/api/shared/app-links")
      .then((r) => { if (alive) setLinks(r.data || {}); })
      .catch(() => { if (alive) setLinks({}); });
    return () => { alive = false; };
  }, []);

  const android = (links?.android_store_url || "").trim();
  const ios = (links?.ios_store_url || "").trim();
  const published = !!(android || ios);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="settings-help-card">
      <h2 className="text-base font-bold text-[#0B2545]">{t("app.help.title")}</h2>
      <p className="mt-0.5 text-sm text-slate-600">{t("app.help.desc")}</p>

      {published ? (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="help-store-buttons">
          {android && (
            <a href={android} target="_blank" rel="noopener noreferrer">
              <Button className="rounded-full bg-[#1B84FF] font-semibold text-white hover:bg-[#0F6FDB]" data-testid="help-android-btn">
                <Smartphone className="mr-2 h-4 w-4" /> {t("app.help.android_download")}
              </Button>
            </a>
          )}
          {ios && (
            <a href={ios} target="_blank" rel="noopener noreferrer">
              <Button className="rounded-full bg-[#0B2545] font-semibold text-white hover:bg-[#123763]" data-testid="help-ios-btn">
                <Apple className="mr-2 h-4 w-4" /> {t("app.help.ios_download")}
              </Button>
            </a>
          )}
        </div>
      ) : (
        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="help-no-store-note">
          <p className="text-sm font-bold text-[#0B2545]">{t("app.help.store_pending_title")}</p>
          <p className="mt-1 text-sm text-slate-700">{t("app.help.store_pending_text")}</p>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <Link to="/como-usar">
          <Button variant="outline" className="rounded-full border-slate-300 font-semibold text-[#0B2545]" data-testid="settings-howto-link">
            <BookOpen className="mr-2 h-4 w-4" /> {t("app.help.howto")}
          </Button>
        </Link>
        <Link to="/contacto">
          <Button variant="outline" className="rounded-full border-slate-300 font-semibold text-[#0B2545]" data-testid="settings-contact-link">
            <LifeBuoy className="mr-2 h-4 w-4" /> {t("app.help.contact")}
          </Button>
        </Link>
      </div>

      <Link
        to="/ayuda/usar-en-navegador"
        className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 underline hover:text-[#0B2545]"
        data-testid="settings-browser-guide-link"
      >
        <Globe className="h-4 w-4" /> {t("app.help.browser_secondary")}
      </Link>
    </div>
  );
};

export default HelpSection;
